import React, { useRef, PropsWithChildren } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { DraggableItemProps } from '@/components/utils/DraggableItem/DraggableItem.types'
import styles from './DraggableItem.module.less'

export const DraggableItem: React.FC<PropsWithChildren<DraggableItemProps>> = ({
    index,
    listId,
    onExternalDrop,
    changeIndex,
    className,
    style,
    onClick,
    ...restProps
}) => {
    const type = DraggableItem.name
    const ref = useRef<HTMLDivElement | null>(null)
    // Callbacks are read through a ref so the drag/drop specs stay stable
    // across re-renders: re-registering a handler MID-DRAG (specs recreated on
    // every render) silently ends the in-flight drag operation, which broke
    // any drag that caused a state update (e.g. drag-hover opening a group)
    const callbacksRef = useRef({ changeIndex, onExternalDrop })
    callbacksRef.current = { changeIndex, onExternalDrop }
    const hasExternalDrop = !!onExternalDrop
    const [{ isOver, dropClassName }, drop] = useDrop<
        { index: number; listId?: string },
        unknown,
        { isOver?: boolean; dropClassName?: string }
    >(
        () => ({
            accept: type,
            collect: (monitor) => {
                const { index: dragIndex } = monitor.getItem() || {}
                const dragListId = monitor.getItem()?.listId
                if (dragListId !== listId) {
                    if (!hasExternalDrop) return {}
                    return {
                        isOver: monitor.isOver(),
                        dropClassName: ` ${styles.dropOverUpward}`,
                    }
                }
                if (dragIndex === index) {
                    return {}
                }
                return {
                    isOver: monitor.isOver(),
                    dropClassName:
                        dragIndex < index
                            ? ` ${styles.dropOverDownward}`
                            : ` ${styles.dropOverUpward}`,
                }
            },
            drop: (item) => {
                if (item.listId !== listId) {
                    callbacksRef.current.onExternalDrop?.(item)
                    return
                }
                callbacksRef.current.changeIndex(item.index, index)
            },
        }),
        [type, index, listId, hasExternalDrop]
    )
    const [, drag] = useDrag(
        () => ({
            type,
            item: { index, listId },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [type, index, listId]
    )
    drop(drag(ref))

    return (
        <div
            ref={ref}
            className={`${className}${isOver ? dropClassName : ''}`}
            style={{ cursor: 'move', ...style }}
            onClick={onClick}
        >
            {restProps.children}
        </div>
    )
}
